import { HttpClient, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, catchError, lastValueFrom, map, Observable, of, timeout } from "rxjs";
import { LocalStorageKey } from "../_helper/local-storage.helper";
import { parseJwt, getFormattedDate, ConvertJPTime, ConvertLocalTimeFromJP, ConvertLocalTimeFromUTC } from "../_helper/utils";
import { ProcessLoadingService } from "./loading.service";
import { environment } from "../../environments/environment";
import { API_ENDPOINTS} from "../constants/api-endpoints";
import { ROUTE_PATH } from "../constants/route-path";
import { ApiService } from "./api.service";
import { TagInfoRO } from "../models/response/taginfo/taginfo.ro.model";
import { TagInfoDTO } from "../models/request/taginfo/taginfo.dto.model";
import { TagInfo } from "../models/taginfo.model";
import { SessionStorageKey } from "../constants/session-storage-key";
import { AuthenticationService } from "./authentication.service";

@Injectable({ providedIn: 'root' })
export class TagsService extends ApiService  {

  private unappliedTagIds = new Set<string>();
  private tagApplyContext = '';

  constructor( http: HttpClient, router: Router, private authenticationService: AuthenticationService) {
      super(http, router);

  }

  get hasUnappliedTags(): boolean {
    return this.tagApplyContext === this.currentTagApplyContext && this.unappliedTagIds.size > 0;
  }

  private get currentTagApplyContext(): string {
    return `${this.authenticationService.getOrgId()}#${this.authenticationService.getMapId()}`;
  }

  private updateTagApplyState(tags: TagInfo[], context: string, replace = false): void {
    if (context !== this.currentTagApplyContext) return;
    if (replace || this.tagApplyContext !== context) {
      this.unappliedTagIds.clear();
    }
    this.tagApplyContext = context;
    for (const tag of tags) {
      const id = `${tag.orgid}#${tag.mid}#${tag.tid}`;
      if (tag.isapply === false) {
        this.unappliedTagIds.add(id);
      } else {
        this.unappliedTagIds.delete(id);
      }
    }
  }
  async getTagMap(mapid: string): Promise<TagInfo[] | null> {
    let pra: TagInfoDTO = {
      mid: mapid
    }
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.LIST_TAG_MAP;
    let resData = await lastValueFrom(this.post<TagInfoRO, TagInfoDTO>(apiUrl, pra));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.listtaginfos) {
      return resData.result.listtaginfos;
    }

    return null;
  }


  async getListTag(): Promise<TagInfo[] | null> {
    const context = this.currentTagApplyContext;
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.LIST_TAG;
    let resData = await lastValueFrom(this.post<TagInfoRO>(apiUrl));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.listtaginfos) {
      this.updateTagApplyState(resData.result.listtaginfos, context, true);
      return resData.result.listtaginfos;
    }

    return null;
  }
  async updateTag(tagInfo: TagInfo): Promise<TagInfo | null> {
    const context = this.currentTagApplyContext;
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.UPDATE_TAG;
    let resData = await lastValueFrom(this.post<TagInfoRO, TagInfo>(apiUrl, tagInfo));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.taginfo) {
      this.updateTagApplyState([resData.result.taginfo], context);
      return resData.result.taginfo;
    }
    return null;
  }
  async addTag(tagInfo: TagInfo): Promise<TagInfo | null> {
    const context = this.currentTagApplyContext;
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.ADD_TAG;
    let resData = await lastValueFrom(this.post<TagInfoRO, TagInfo>(apiUrl, tagInfo));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.taginfo) {
      this.updateTagApplyState([resData.result.taginfo], context);
      return resData.result.taginfo;
    }
    return null;
  }
  async applyTag(): Promise<boolean | null> {
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.APPLY_TAG;
    let resData = await lastValueFrom(this.post<TagInfoRO, boolean>(apiUrl).pipe(timeout(60_000)));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.success) {
      return true;
    }
    return false;
  }
  async getByTagID(tid:string): Promise<TagInfo[] | null>{
    let pra: TagInfoDTO = {
      tid: tid
    }
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.GET_BY_TAGID;
    let resData = await lastValueFrom(this.post<TagInfoRO, TagInfoDTO>(apiUrl, pra));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.listtaginfos) {
      return resData.result.listtaginfos;
    }

    return null;
  }
  async deleteTag(tagInfo: TagInfo): Promise<boolean | null> {
    let apiUrl = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.DELETE_TAG;
    let resData = await lastValueFrom(this.post<TagInfoRO, TagInfo>(apiUrl, tagInfo));

    if (resData && resData.status === HttpStatusCode.Ok && resData.result && resData.result.taginfo) {
      return resData?.result?.success ?? false;
    }
    return null;
  }
  async batteryTag(payload: any) {
    let url = API_ENDPOINTS.TAG_INFO.CONTROLLER + API_ENDPOINTS.TAG_INFO.BATTERY_TAG;
    let resData = await lastValueFrom(this.post(url,payload));
    if (!resData) {
        return null;
    }
    return resData.result;
  }
 
  getTagListData(payload: {
        mapId: string,
        compId: string,
        orgId: string
    }): Observable<any> {
      const url = API_ENDPOINTS.SETTINGS.CONTROLLER + API_ENDPOINTS.SETTINGS.LIST_TAG_DATA;
      return this.http.post(url, payload).pipe(
        catchError(err => of(err)),
        map((response: any) => {
        if (response.code === 200) {
            return response.result;
        }
        return null;
        }));
  }

  syncListUserOnTrak(): Observable<any> {
    let payload:any = {
      // orgid : ORG_ID,
      // compid: COMP_ID,
      // mid: MAP_ID
    }
    const url = API_ENDPOINTS.SYNCDATA.CONTROLLER + API_ENDPOINTS.SYNCDATA.SYNC_LIST_USER;
    return this.http.post(url, payload).pipe(
      catchError(err => of(err)),
      map((response: any) => {
      if (response.statusCode === 200) {
        return response.result;
      }
      return null;
    }));
  }

  syncTagMasterData(tags: any): Observable<any> {
    // let payload:any = {
    //   orgid : ORG_ID,
    //   compid: COMP_ID,
    //   mid: MAP_ID,
    //   tagid: tags
    // }
    let payload:any = {

    }
    const url = API_ENDPOINTS.SYNCDATA.CONTROLLER + API_ENDPOINTS.SYNCDATA.SYNC_TAG;
    return this.http.post(url, payload).pipe(
      catchError(err => of(err)),
      map((response: any) => {
      if (response === 1) {
        return true;
      }
      return false;
    }));
  }

  updateCacheTagPosition(tags: TagInfo[]) {
    if (!tags || tags.length === 0) return;

    // Deduplicate: keep only the latest tag (highest timestamp) per tid
    const latestMap = new Map<string, TagInfo>();
    tags.forEach(tag => {
      const existing = latestMap.get(tag.tid!);
      if (!existing || (tag.timestamp ?? '') > (existing.timestamp ?? '')) {
        latestMap.set(tag.tid!, tag);
      }
    });

    let cachedTags = sessionStorage.getItem(SessionStorageKey.TAG_POSITION);
    let cachedTagsArr: TagInfo[] = cachedTags ? JSON.parse(cachedTags) as TagInfo[] : [];

    // Map cache by tid for O(1) lookup
    const cacheMap = new Map<string, number>();
    cachedTagsArr.forEach((c, i) => cacheMap.set(c.tid!, i));

    latestMap.forEach((tag, tid) => {
      const index = cacheMap.get(tid);
      if (index !== undefined) {
        let cachedTime = cachedTagsArr[index].timestamp ?? '';
        if ((tag.timestamp ?? '') > cachedTime) {
          cachedTagsArr[index].x = tag.x;
          cachedTagsArr[index].y = tag.y;
          cachedTagsArr[index].mid = tag.mid;
          cachedTagsArr[index].floorid = tag.floorid;
          cachedTagsArr[index].timestamp = tag.timestamp;
        }
      } else {
        cacheMap.set(tid, cachedTagsArr.length);
        cachedTagsArr.push(tag);
      }
    });

    sessionStorage.setItem(SessionStorageKey.TAG_POSITION, JSON.stringify(cachedTagsArr));
  }

  getCacheTagPosition(mapid?: string): TagInfo[] {
    let cache = sessionStorage.getItem(SessionStorageKey.TAG_POSITION);
    if (!cache) return [];
    let tags = JSON.parse(cache) as TagInfo[];
    const nowJP = ConvertJPTime(ConvertLocalTimeFromUTC(new Date()));
    const minus10min = new Date(nowJP.getTime() - 10 * 60 * 1000);
    const minus10minStr = getFormattedDate(minus10min);
    return tags.filter(tag => {
      if (!mapid || tag.mid !== mapid || !tag.x || !tag.y) return false;
      if (!tag.timestamp) return false;
      return tag.timestamp >= minus10minStr;
    });
  }
}
